// src/pages/MeetingInventory.js
// Post-meeting checklist: each item is a true/false checkbox.
// Scores (0–5) accumulate across up to 5 meetings per item.

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, orderBy, getDocs, serverTimestamp } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { MEETING_INVENTORY, computeTopics, compressTimeGaps } from '../utils/metrics';
import { parseQuotes, formatDuration, ALL_CODES } from '../utils/shortcodes';
import { ROLES_BY_CATEGORY, ALL_ROLES_BY_ID } from '../utils/roles';
import PageLoading from '../components/PageLoading';

// ─── Checkbox Item Row ────────────────────────────────────────────────────────

function CheckboxItem({ item, index, checked, onChange }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 14, paddingBottom: 14, cursor: 'pointer' }}>
      <div style={{ paddingTop: 2 }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={e => onChange(item.id, e.target.checked)}
          style={{ width: 18, height: 18, accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
        />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--ink-muted)', background: 'var(--bg-soft)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 }}>
            {index + 1}
          </span>
          <span style={{ fontSize: '0.875rem', color: checked ? 'var(--ink)' : 'var(--ink-soft)', lineHeight: 1.5, fontWeight: checked ? 600 : 400 }}>
            {item.text}
          </span>
        </div>
      </div>
    </label>
  );
}

// ─── Group Card ───────────────────────────────────────────────────────────────

function GroupCard({ groupKey, group, checks, historyCounts, onChange }) {
  const checkedCount = group.items.filter(i => checks[i.id]).length;
  const pct = Math.round((checkedCount / group.items.length) * 100);

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div className="card-title" style={{ color: group.color }}>{group.label}</div>
          <div className="card-subtitle" style={{ marginBottom: 0 }}>{group.items.length} behaviors · check all that were observed</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: 600, color: pct >= 70 ? 'var(--accent)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)' }}>
            {checkedCount}<span style={{ fontSize: '1rem', color: 'var(--ink-muted)', fontFamily: 'var(--font-body)' }}>/{group.items.length}</span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', fontWeight: 700 }}>this meeting</div>
        </div>
      </div>
      <div className="progress-bar" style={{ marginBottom: 20 }}>
        <div className="progress-fill" style={{ width: `${pct}%`, background: pct >= 70 ? 'var(--accent)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {group.items.map((item, i) => (
          <div key={item.id} style={{ borderBottom: i < group.items.length - 1 ? '1px solid var(--border)' : 'none', paddingBottom: i < group.items.length - 1 ? 14 : 0 }}>
            <CheckboxItem item={item} index={i} checked={checks[item.id] || false} historyCounts={historyCounts} onChange={onChange} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Roles Section ────────────────────────────────────────────────────────────

function RolesSection({ categoryKey, attendees, observedRoles, onChange }) {
  const roles = ROLES_BY_CATEGORY[categoryKey] || [];
  if (roles.length === 0) return null;

  const handleRoleToggle = (roleId, checked) => {
    if (checked) {
      onChange(prev => ({ ...prev, [roleId]: prev[roleId] || [] }));
    } else {
      onChange(prev => {
        const next = { ...prev };
        delete next[roleId];
        return next;
      });
    }
  };

  const handleAttendeeToggle = (roleId, name) => {
    onChange(prev => {
      const current = prev[roleId] || [];
      const next = current.includes(name)
        ? current.filter(n => n !== name)
        : [...current, name];
      return { ...prev, [roleId]: next };
    });
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title" style={{ marginBottom: 4 }}>Roles Observed</div>
      <div className="card-subtitle" style={{ marginBottom: 20 }}>
        Check any roles you observed. Select which attendees showed each tendency.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {roles.map((role, i) => {
          const checked = Object.prototype.hasOwnProperty.call(observedRoles, role.id);
          const tagged = observedRoles[role.id] || [];
          return (
            <div
              key={role.id}
              style={{
                borderBottom: i < roles.length - 1 ? '1px solid var(--border)' : 'none',
                paddingBottom: 14,
                paddingTop: i > 0 ? 14 : 0,
              }}
            >
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={e => handleRoleToggle(role.id, e.target.checked)}
                  style={{ marginTop: 3, width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer', flexShrink: 0 }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: checked ? 'var(--ink)' : 'var(--ink-soft)', lineHeight: 1.4 }}>
                    {role.name}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--ink-muted)', lineHeight: 1.5, marginTop: 2 }}>
                    {role.description}
                  </div>
                </div>
              </label>
              {checked && attendees.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10, paddingLeft: 28 }}>
                  {attendees.map(name => {
                    const active = tagged.includes(name);
                    return (
                      <button
                        key={name}
                        onClick={() => handleAttendeeToggle(role.id, name)}
                        style={{
                          padding: '3px 10px',
                          borderRadius: 100,
                          border: `1.5px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                          background: active ? 'var(--olive-glow)' : 'var(--bg-soft)',
                          color: active ? 'var(--olive)' : 'var(--ink-muted)',
                          fontSize: '0.75rem',
                          fontWeight: active ? 700 : 400,
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Topics Feedback ──────────────────────────────────────────────────────────

function NumericScoreToggle({ value, onChange }) {
  const isNA = value === null || value === 'na';
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {[1, 2, 3, 4, 5].map(v => (
        <button key={v} onClick={() => onChange(v === value ? 0 : v)} style={{
          width: 36, height: 36, borderRadius: 6,
          border: `1.5px solid ${value === v ? 'var(--navy)' : 'var(--border)'}`,
          background: value === v ? 'var(--navy)' : 'transparent',
          color: value === v ? 'white' : 'var(--ink-muted)',
          fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer',
          transition: 'var(--transition)', fontFamily: 'var(--font-body)',
        }}>{v}</button>
      ))}
      <button onClick={() => onChange(isNA ? 0 : 'na')} style={{
        padding: '0 10px', height: 36, borderRadius: 6,
        border: `1.5px solid ${isNA ? 'var(--ink-muted)' : 'var(--border)'}`,
        background: isNA ? 'var(--ink-muted)' : 'transparent',
        color: isNA ? 'white' : 'var(--ink-muted)',
        fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
        transition: 'var(--transition)', fontFamily: 'var(--font-body)',
      }}>N/A</button>
    </div>
  );
}

function TopicsFeedback({ entries, topicFeedback, onChange, onRename }) {
  const topics = [];
  const seen = new Set();
  (entries || []).forEach(e => {
    if (e.topic && !seen.has(e.topic)) { seen.add(e.topic); topics.push(e.topic); }
  });
  const [editingTopic, setEditingTopic] = useState(null);
  const [editValue, setEditValue] = useState('');

  const commitRename = (oldName) => {
    onRename(oldName, editValue);
    setEditingTopic(null);
  };

  const topicStatsMap = {};
  computeTopics(compressTimeGaps(entries || [])).forEach(t => {
    const speakerMap = {};
    (t.entries || []).forEach(e => {
      if (!e.speakerName) return;
      const name = e.speakerDisplayName || e.speakerName.split('@')[0];
      const ms = (e.endTime && e.startTime) ? e.endTime - e.startTime : 0;
      speakerMap[name] = (speakerMap[name] || 0) + ms;
    });
    topicStatsMap[t.label] = {
      ms: t.ms, speakers: t.speakers, questions: t.questions,
      speakerList: Object.entries(speakerMap).sort((a, b) => b[1] - a[1]),
      genius: t.genius || {},
    };
  });

  if (topics.length === 0) {
    return (
      <div className="card">
        <div className="card-title">Topics Feedback</div>
        <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>No topics were tagged in this meeting.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title">Topics Feedback</div>
      <div className="card-subtitle">Rate each topic on decision-making and debate quality (1–5, or N/A if not applicable)</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
        {topics.map((topic, idx) => {
          const topicData = topicFeedback[topic] || { decision: 0, debate: 0 };
          const stats = topicStatsMap[topic] || { ms: 0, speakers: 0, questions: 0, speakerList: [], genius: {} };
          const geniusList = Object.entries(stats.genius)
            .map(([code, count]) => ({ code, count, def: ALL_CODES[code] }))
            .filter(g => g.def && g.count > 0);
          return (
            <div key={topic} style={{ padding: 16, background: 'var(--bg-soft)', borderRadius: 8, borderLeft: '4px solid var(--warning)', borderBottom: idx < topics.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                {editingTopic === topic ? (
                  <input
                    autoFocus
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commitRename(topic);
                      if (e.key === 'Escape') setEditingTopic(null);
                    }}
                    onBlur={() => commitRename(topic)}
                    style={{
                      fontSize: '1rem', fontWeight: 600, color: 'var(--ink)',
                      border: '1.5px solid var(--warning)', borderRadius: 6,
                      padding: '2px 8px', background: 'var(--bg-card)',
                      outline: 'none', minWidth: 160,
                    }}
                  />
                ) : (
                  <>
                    <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--ink)' }}># {topic}</span>
                    <button
                      onClick={() => { setEditingTopic(topic); setEditValue(topic); }}
                      title="Rename topic"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-muted)', fontSize: '0.8rem', padding: '0 2px', lineHeight: 1 }}
                    >✎</button>
                  </>
                )}
              </div>
              <div style={{ display: 'flex', gap: 16, fontSize: '0.82rem', color: 'var(--ink-muted)', marginBottom: 10 }}>
                <span>⏱ {stats.ms > 0 ? formatDuration(stats.ms) : '—'}</span>
                <span>👥 {stats.speakers} speaker{stats.speakers !== 1 ? 's' : ''}</span>
                <span>❓ {stats.questions} question{stats.questions !== 1 ? 's' : ''}</span>
              </div>
              {stats.speakerList.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                  {stats.speakerList.map(([name, ms]) => (
                    <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: '0.75rem', color: 'var(--ink)' }}>
                      {name}
                      {ms > 0 && <span style={{ color: 'var(--ink-muted)' }}>· {formatDuration(ms)}</span>}
                    </span>
                  ))}
                </div>
              )}
              {geniusList.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 14 }}>
                  {geniusList.map(({ code, count, def }) => (
                    <span key={code} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', background: def.color + '1a', border: `1px solid ${def.color}33`, borderRadius: 10, fontSize: '0.72rem', fontWeight: 600, color: def.color }}>
                      {def.emoji} {def.label}{count > 1 ? ` ×${count}` : ''}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: 8, display: 'block' }}>Level of Debate (1–5)</label>
                <NumericScoreToggle value={topicData.debate} onChange={v => onChange(topic, { ...topicData, debate: v })} />
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 6 }}>1 = No debate · 5 = High level of debate</div>
              </div>
              <div>
                <label style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--ink)', marginBottom: 8, display: 'block' }}>Level of Decision (1–5)</label>
                <NumericScoreToggle value={topicData.decision} onChange={v => onChange(topic, { ...topicData, decision: v })} />
                <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 6 }}>1 = No decision · 5 = Clear decision reached</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Participant Feedback ─────────────────────────────────────────────────────

function ParticipantFeedback({ participantFeedback, onChange, observedRoles = {} }) {
  const participants = Object.entries(participantFeedback);
  const roleById = ALL_ROLES_BY_ID;

  if (participants.length === 0) {
    return (
      <div className="card">
        <div className="card-title">Participant Feedback</div>
        <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>No attendees found for this meeting.</p>
      </div>
    );
  }

  const AVATAR_COLORS = ['#2a3d5b', '#5f7942', '#cba43a', '#922b3f', '#7209b7', '#4cc9f0', '#e76f51', '#4361ee'];
  const avatarColor = (name = '') => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-title">Participant Feedback</div>
      <div className="card-subtitle">
        Notes are pre-filled from your <code style={{ fontSize: '0.8rem', background: 'var(--bg-soft)', padding: '1px 5px', borderRadius: 4 }}>/c</code> comments during the meeting. Add or edit feedback for each participant.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
        {participants.map(([speakerName, data], idx) => {
          const name = data.displayName || speakerName.split('@')[0];
          const color = avatarColor(speakerName);
          const hasAutoNotes = !!(data.notes && data.notes.trim());
          const assignedRoles = Object.entries(observedRoles)
            .filter(([, names]) => names.includes(name))
            .map(([roleId]) => roleById[roleId]?.name)
            .filter(Boolean);
          return (
            <div key={speakerName} style={{
              padding: 16, background: 'var(--bg-soft)', borderRadius: 8,
              borderLeft: `4px solid ${color}`,
              borderBottom: idx < participants.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: color, color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.8rem', fontWeight: 700, flexShrink: 0,
                }}>
                  {name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--ink)' }}>{name}</div>
                  {hasAutoNotes && (
                    <div style={{ fontSize: '0.68rem', color: '#9b5de5', fontWeight: 600, marginTop: 1 }}>
                      📝 Pre-filled from /c notes
                    </div>
                  )}
                </div>
              </div>
              <textarea
                value={data.notes || ''}
                onChange={e => onChange(speakerName, { ...data, notes: e.target.value })}
                placeholder={`Add feedback for ${name}...`}
                style={{
                  width: '100%', minHeight: 90, padding: '12px 14px',
                  border: `1.5px solid ${hasAutoNotes ? '#9b5de530' : 'var(--border)'}`,
                  borderRadius: 8,
                  fontFamily: 'var(--font-body)', fontSize: '0.875rem',
                  color: 'var(--ink)', background: 'var(--bg-card)',
                  outline: 'none', resize: 'vertical', lineHeight: 1.6,
                }}
                onFocus={e => e.target.style.borderColor = color}
                onBlur={e => e.target.style.borderColor = hasAutoNotes ? '#9b5de530' : 'var(--border)'}
              />
              {assignedRoles.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                  {assignedRoles.map(role => (
                    <span key={role} style={{
                      fontSize: '0.72rem', fontWeight: 600, padding: '2px 8px',
                      borderRadius: 100, background: `${color}18`, color,
                      border: `1px solid ${color}33`,
                    }}>
                      {role}
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Quote Selection ──────────────────────────────────────────────────────────

function QuoteSelection({ meeting, selectedQuotes, onChange }) {
  // Extract all quotes from entries using the same parseQuotes utility
  const allQuotes = [];
  (meeting.entries || []).forEach((entry, entryIdx) => {
    const q = parseQuotes(entry.text || '');
    const speaker = entry.speakerDisplayName || entry.speakerName?.split('@')[0] || 'Unknown';
    q.good.forEach((text, i) => allQuotes.push({ id: `good-${entryIdx}-${i}`, text, type: 'good', speaker }));
    q.bad.forEach((text, i) => allQuotes.push({ id: `bad-${entryIdx}-${i}`, text, type: 'bad', speaker }));
  });

  const goodQuotes = allQuotes.filter(q => q.type === 'good');
  const badQuotes = allQuotes.filter(q => q.type === 'bad');
  const selectedCount = Object.values(selectedQuotes).filter(Boolean).length;

  const toggle = (id) => onChange(prev => ({ ...prev, [id]: !prev[id] }));

  if (allQuotes.length === 0) {
    return (
      <div className="card">
        <div className="card-title">Quote Selection</div>
        <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem' }}>
          No quotes were captured in this meeting. Use{' '}
          <code style={{ fontSize: '0.8rem', background: 'var(--bg-soft)', padding: '1px 5px', borderRadius: 4 }}>"quote"</code>{' '}
          for productive quotes and{' '}
          <code style={{ fontSize: '0.8rem', background: 'var(--bg-soft)', padding: '1px 5px', borderRadius: 4 }}>-"quote"</code>{' '}
          for unproductive ones during recording.
        </p>
      </div>
    );
  }

  const QuoteCard = ({ quote }) => {
    const selected = !!selectedQuotes[quote.id];
    const isGood = quote.type === 'good';
    const color = isGood ? '#5f7942' : '#922b3f';
    return (
      <label style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '12px 16px',
        background: selected
          ? (isGood ? 'rgba(95,121,66,0.12)' : 'rgba(146,43,63,0.12)')
          : (isGood ? 'rgba(95,121,66,0.04)' : 'rgba(146,43,63,0.04)'),
        borderLeft: `3px solid ${color}`,
        border: `1px solid ${selected ? color + '55' : 'transparent'}`,
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={() => toggle(quote.id)}
          style={{ marginTop: 3, accentColor: color, cursor: 'pointer', flexShrink: 0 }}
        />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--ink)', lineHeight: 1.55, marginBottom: 4 }}>
            {isGood ? '\u201c' : '\u2018'}{quote.text}{isGood ? '\u201d' : '\u2019'}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--ink-muted)' }}>— {quote.speaker}</div>
        </div>
      </label>
    );
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <div className="card-title" style={{ margin: 0 }}>Quote Selection</div>
        {selectedCount > 0 && (
          <span style={{
            fontSize: '0.75rem', fontWeight: 700,
            padding: '3px 10px', borderRadius: 100,
            background: 'var(--olive-glow)', color: 'var(--olive)',
          }}>
            {selectedCount} selected
          </span>
        )}
      </div>
      <div className="card-subtitle">Select quotes to share with the team leader in the meeting report</div>

      {goodQuotes.length > 0 && (
        <>
          <div style={{
            fontSize: '0.72rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            color: '#5f7942', margin: '20px 0 10px',
          }}>
            ✅ Productive Quotes
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {goodQuotes.map(q => <QuoteCard key={q.id} quote={q} />)}
          </div>
        </>
      )}

      {badQuotes.length > 0 && (
        <>
          <div style={{
            fontSize: '0.72rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em',
            color: '#922b3f', margin: '20px 0 10px',
          }}>
            ❌ Unproductive Quotes
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {badQuotes.map(q => <QuoteCard key={q.id} quote={q} />)}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MeetingInventory() {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(1);

  const [checks, setChecks] = useState({});
  const [historyCounts, setHistoryCounts] = useState({});
  const [activeGroup, setActiveGroup] = useState('compellingContent');
  const [sectionFeedback, setSectionFeedback] = useState({});
  const [topicFeedback, setTopicFeedback] = useState({});
  const [participantFeedback, setParticipantFeedback] = useState({});
  const [selectedQuotes, setSelectedQuotes] = useState({});
  const [observedRoles, setObservedRoles] = useState({});
  const [entries, setEntries] = useState([]);
  const [meetingFeedback, setMeetingFeedback] = useState('');
  const [keyMomentNotes, setKeyMomentNotes] = useState({});

  const TOTAL_STEPS = 4;

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'meetings', meetingId));
        if (!snap.exists()) { setLoading(false); return; }
        const data = { id: snap.id, ...snap.data() };
        setMeeting(data);
        setEntries(data.entries || []);

        if (data.inventoryChecks) setChecks(data.inventoryChecks);
        if (data.sectionFeedback) setSectionFeedback(data.sectionFeedback);
        if (data.topicFeedback) setTopicFeedback(data.topicFeedback);
        if (data.participantFeedback) setParticipantFeedback(data.participantFeedback);
        if (data.selectedQuotes) setSelectedQuotes(data.selectedQuotes);
        if (data.observedRoles) setObservedRoles(data.observedRoles);

        // Pre-fill /f feedback if not already saved
        if (data.meetingFeedback) {
          setMeetingFeedback(data.meetingFeedback);
        } else {
          const fText = (data.entries || [])
            .filter(e => e.type !== 'break' && e.text?.includes('/f'))
            .map(e => (e.text || '').replace(/\/f\b/g, '').replace(/\s+/g, ' ').trim())
            .filter(Boolean).join('\n');
          setMeetingFeedback(fText);
        }
        if (data.keyMomentNotes) setKeyMomentNotes(data.keyMomentNotes);

        setLoading(false);

        if (data.teamId) {
          getDocs(
            query(
              collection(db, 'meetings'),
              where('teamId', '==', data.teamId),
              where('status', 'in', ['completed', 'reviewed']),
              orderBy('createdAt', 'desc')
            )
          ).then(prevSnap => {
            const prevMeetings = prevSnap.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter(m => m.id !== meetingId)
              .slice(0, 4);

            const counts = {};
            prevMeetings.forEach(m => {
              Object.entries(m.inventoryChecks || {}).forEach(([itemId, wasChecked]) => {
                if (wasChecked) counts[itemId] = (counts[itemId] || 0) + 1;
              });
            });
            setHistoryCounts(counts);
          }).catch(err => console.warn('Could not load meeting history:', err));
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    }
    load();
  }, [meetingId]);

  const handleCheck = (itemId, value) => setChecks(prev => ({ ...prev, [itemId]: value }));
  const handleTopicChange = (topic, data) => setTopicFeedback(prev => ({ ...prev, [topic]: data }));
  const handleParticipantChange = (speakerName, data) => setParticipantFeedback(prev => ({ ...prev, [speakerName]: data }));

  const handleRenameTopic = async (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    const updated = entries.map(e => e.topic === oldName ? { ...e, topic: trimmed } : e);
    setEntries(updated);
    setTopicFeedback(prev => {
      const next = { ...prev, [trimmed]: prev[oldName] };
      delete next[oldName];
      return next;
    });
    try {
      await updateDoc(doc(db, 'meetings', meetingId), { entries: updated, updatedAt: serverTimestamp() });
    } catch (err) { console.error('Failed to rename topic:', err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'meetings', meetingId), {
        inventoryChecks: checks,
        sectionFeedback,
        topicFeedback,
        participantFeedback,
        selectedQuotes,
        observedRoles,
        meetingFeedback: meetingFeedback.trim() || null,
        keyMomentNotes,
        updatedAt: serverTimestamp(),
      });
      navigate(`/meetings/${meetingId}/report`);
    } catch (err) {
      console.error(err);
      setSaving(false);
    }
  };

  if (loading) return <PageLoading text="Loading inventory..." />;
  if (!meeting) return <p style={{ color: 'var(--ink-muted)', padding: 48 }}>Meeting not found.</p>;

  const groupKeys = Object.keys(MEETING_INVENTORY);
  const totalChecked = Object.values(checks).filter(Boolean).length;
  const totalItems = groupKeys.reduce((sum, k) => sum + MEETING_INVENTORY[k].items.length, 0);

  const stepTitles = ['Meeting Inventory', 'Topics Feedback', 'Participant Feedback', 'Quote Selection'];
  const stepSubtitles = [
    'Check each behavior you observed in this meeting. Scores build up across 5 meetings.',
    'Rate each topic on decision-making and debate quality.',
    'Review and add individual feedback for each participant.',
    'Choose which quotes to share with the team leader in the report.',
  ];

  // Navigation logic
  const activeGroupIdx = groupKeys.indexOf(activeGroup);
  const isLastGroup = activeGroupIdx === groupKeys.length - 1;
  const isFirstGroup = activeGroupIdx === 0;

  const handleNext = () => {
    if (step === 1 && !isLastGroup) setActiveGroup(groupKeys[activeGroupIdx + 1]);
    else if (step < TOTAL_STEPS) setStep(step + 1);
  };
  const handleBack = () => {
    if (step === 1 && !isFirstGroup) setActiveGroup(groupKeys[activeGroupIdx - 1]);
    else if (step > 1) {
      if (step === 2) setActiveGroup(groupKeys[groupKeys.length - 1]);
      setStep(step - 1);
    }
  };

  const showBack = step > 1 || !isFirstGroup;
  const showSave = step === TOTAL_STEPS;
  const showNext = !showSave;

  const selectedQuoteCount = Object.values(selectedQuotes).filter(Boolean).length;

  return (
    <div className="fade-up" style={{ maxWidth: 900 }}>
      <div className="report-header" style={{ marginBottom: 28 }}>
        <div className="report-header-meta">Step {step} of {TOTAL_STEPS} · Post-Meeting Review · {meeting.teamName}</div>
        <h2>{stepTitles[step - 1]}</h2>
        <div className="report-header-sub">{stepSubtitles[step - 1]}</div>

        {step === 1 && (
          <>
            <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(255,255,255,0.07)', borderRadius: 8, fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
              <strong style={{ color: 'rgba(255,255,255,0.85)' }}>How it works:</strong>
              {' '}Check each behavior you observed. Each item tracks how often it appears across your last 5 meetings, giving a score of 0–5.
            </div>
            <div className="grid-3" style={{ marginTop: 16 }}>
              <div>
                <div className="report-stat-label">Checked This Meeting</div>
                <div className="report-stat-value">{totalChecked}<span style={{ fontSize: '1rem', opacity: 0.5 }}>/{totalItems}</span></div>
              </div>
              <div>
                <div className="report-stat-label">Previous Meetings</div>
                <div className="report-stat-value">{Math.min(Object.keys(historyCounts).length > 0 ? 4 : 0, 4)}<span style={{ fontSize: '1rem', opacity: 0.5 }}>/4</span></div>
              </div>
              <div>
                <div className="report-stat-label">Scoring Method</div>
                <div className="report-stat-value" style={{ fontSize: '1.4rem' }}>0–5</div>
              </div>
            </div>
          </>
        )}

        {step === 4 && selectedQuoteCount > 0 && (
          <div style={{ marginTop: 12, fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' }}>
            {selectedQuoteCount} quote{selectedQuoteCount !== 1 ? 's' : ''} selected to share with team leader
          </div>
        )}
      </div>

      {step === 1 && (
        <>
          <div className="report-tabs" style={{ marginBottom: 24 }}>
            {groupKeys.map(key => {
              const group = MEETING_INVENTORY[key];
              const groupChecked = group.items.filter(i => checks[i.id]).length;
              return (
                <button key={key} onClick={() => setActiveGroup(key)} className={`report-tab${activeGroup === key ? ' active' : ''}`}>
                  {group.label}
                  {groupChecked > 0 && (
                    <span style={{ marginLeft: 6, fontSize: '0.65rem', background: activeGroup === key ? 'rgba(255,255,255,0.25)' : 'var(--olive-glow)', color: activeGroup === key ? 'white' : 'var(--olive)', padding: '1px 6px', borderRadius: 10, fontWeight: 700 }}>
                      {groupChecked}/{group.items.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <GroupCard
            key={activeGroup}
            groupKey={activeGroup}
            group={MEETING_INVENTORY[activeGroup]}
            checks={checks}
            historyCounts={historyCounts}
            onChange={handleCheck}
          />

          <RolesSection
            categoryKey={activeGroup}
            attendees={(meeting.attendees || []).map(a => a.displayName || a.name || a)}
            observedRoles={observedRoles}
            onChange={setObservedRoles}
          />

          {totalChecked > 0 && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-title" style={{ marginBottom: 16 }}>Running Summary</div>
              <div className="grid-2">
                {groupKeys.map(key => {
                  const g = MEETING_INVENTORY[key];
                  const count = g.items.filter(i => checks[i.id]).length;
                  const pct = Math.round((count / g.items.length) * 100);
                  return (
                    <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: 8, background: 'var(--bg-soft)' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{g.label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 60 }}>
                          <div className="progress-bar" style={{ marginBottom: 0 }}>
                            <div className="progress-fill" style={{ width: `${pct}%`, background: pct >= 70 ? 'var(--accent)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)' }} />
                          </div>
                        </div>
                        <span style={{ fontSize: '0.85rem', fontWeight: 700, color: pct >= 70 ? 'var(--accent)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)', minWidth: 36, textAlign: 'right' }}>
                          {count}/{g.items.length}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {step === 2 && <TopicsFeedback entries={entries} topicFeedback={topicFeedback} onChange={handleTopicChange} onRename={handleRenameTopic} />}
      {step === 3 && (
        <>
          {/* Meeting Feedback (/f) */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title">Meeting Feedback</div>
            <div className="card-subtitle">
              Pre-filled from <code style={{ fontSize: '0.8rem', background: 'var(--bg-soft)', padding: '1px 5px', borderRadius: 4 }}>/f</code> entries. Edit to condense into final notes.
            </div>
            <textarea
              value={meetingFeedback}
              onChange={e => setMeetingFeedback(e.target.value)}
              rows={5}
              style={{
                width: '100%', marginTop: 16, padding: '12px 14px', borderRadius: 8,
                border: '1.5px solid var(--border)', background: 'var(--bg-soft)',
                color: 'var(--ink)', fontSize: '0.9rem', lineHeight: 1.6,
                resize: 'vertical', boxSizing: 'border-box',
              }}
              placeholder="Condense your meeting feedback here…"
            />
          </div>

          {/* Key Moments (/km) */}
          {entries.filter(e => e.type !== 'break' && e.text?.includes('/km') && e.startTime).length > 0 && (() => {
            const kmEntries = entries.filter(e => e.type !== 'break' && e.text?.includes('/km') && e.startTime);
            return (
              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-title">Key Moments</div>
                <div className="card-subtitle">Add context for each <code style={{ fontSize: '0.8rem', background: 'var(--bg-soft)', padding: '1px 5px', borderRadius: 4 }}>/km</code> marker from the recording.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
                  {kmEntries.map(e => {
                    const WINDOW = 2 * 60 * 1000;
                    const t = e.startTime;
                    const speakerMap = {};
                    entries.forEach(en => {
                      if (en.type === 'break' || !en.startTime || !en.speakerName) return;
                      if (en.startTime >= t - WINDOW && en.startTime <= t + WINDOW) {
                        const name = en.speakerDisplayName || en.speakerName.split('@')[0];
                        const ms = (en.endTime && en.startTime) ? en.endTime - en.startTime : 0;
                        speakerMap[name] = (speakerMap[name] || 0) + ms;
                      }
                    });
                    const context = Object.entries(speakerMap).sort((a, b) => b[1] - a[1]);
                    const timeStr = new Date(e.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    const cleanText = (e.text || '').replace(/\/km\b/g, '').replace(/\s+/g, ' ').trim();
                    return (
                      <div key={e.id} style={{ padding: 16, background: 'var(--bg-soft)', borderRadius: 8, borderLeft: '4px solid #4361ee' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                          <span style={{ color: '#4361ee', fontWeight: 700, fontSize: '0.85rem' }}>★ {timeStr}</span>
                          {cleanText && <span style={{ color: 'var(--ink-muted)', fontSize: '0.82rem' }}>{cleanText}</span>}
                        </div>
                        {context.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                            <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)', marginRight: 4 }}>±2 min:</span>
                            {context.map(([name, ms]) => (
                              <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 9px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, fontSize: '0.75rem', color: 'var(--ink)' }}>
                                {name}{ms > 0 && <span style={{ color: 'var(--ink-muted)' }}> · {formatDuration(ms)}</span>}
                              </span>
                            ))}
                          </div>
                        )}
                        <textarea
                          value={keyMomentNotes[e.id] || ''}
                          onChange={ev => setKeyMomentNotes(prev => ({ ...prev, [e.id]: ev.target.value }))}
                          rows={3}
                          style={{
                            width: '100%', padding: '10px 12px', borderRadius: 6,
                            border: '1.5px solid var(--border)', background: 'var(--bg-card)',
                            color: 'var(--ink)', fontSize: '0.87rem', lineHeight: 1.5,
                            resize: 'vertical', boxSizing: 'border-box',
                          }}
                          placeholder="What was significant about this moment?"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Participant Coaching (/c) */}
          <ParticipantFeedback participantFeedback={participantFeedback} onChange={handleParticipantChange} observedRoles={observedRoles} />
        </>
      )}
      {step === 4 && <QuoteSelection meeting={meeting} selectedQuotes={selectedQuotes} onChange={setSelectedQuotes} />}

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingBottom: 16 }}>
        {showBack && <button className="btn btn-secondary" onClick={handleBack}>← Back</button>}
        {showNext && <button className="btn btn-primary" onClick={handleNext}>Next step →</button>}
        {showSave && (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ minWidth: 160 }}>
            {saving ? <><span className="spinner" /> Saving...</> : 'Save & view report →'}
          </button>
        )}
      </div>

      {/* Progress circles */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, paddingBottom: 48, flexWrap: 'wrap' }}>
        {groupKeys.map((key, i) => {
          const isDone = step > 1 || activeGroupIdx > i;
          const isActive = step === 1 && activeGroup === key;
          return (
            <button
              key={key}
              title={MEETING_INVENTORY[key].label}
              onClick={() => { setStep(1); setActiveGroup(key); }}
              style={{
                width: 32, height: 32, borderRadius: '50%', border: 'none',
                cursor: 'pointer', fontSize: '0.65rem', fontWeight: 700,
                background: isActive ? '#cba43a' : isDone ? '#cba43a88' : 'var(--bg-soft)',
                color: isActive || isDone ? 'white' : 'var(--ink-muted)',
                outline: isActive ? '2px solid #cba43a' : 'none',
                outlineOffset: 2,
                transition: 'background 0.15s',
                flexShrink: 0,
              }}
            >
              {i + 1}
            </button>
          );
        })}
        <div style={{ width: 20, height: 2, background: 'var(--border)', borderRadius: 1, flexShrink: 0 }} />
        {[2, 3, 4].map((s, i) => {
          const isDone = step > s;
          const isActive = step === s;
          return (
            <button
              key={s}
              title={stepTitles[s - 1]}
              onClick={() => { if (isDone || isActive) setStep(s); }}
              style={{
                width: 32, height: 32, borderRadius: '50%', border: 'none',
                cursor: isDone || isActive ? 'pointer' : 'default',
                fontSize: '0.65rem', fontWeight: 700,
                background: isActive ? '#4361ee' : isDone ? '#4361ee88' : 'var(--bg-soft)',
                color: isActive || isDone ? 'white' : 'var(--ink-muted)',
                outline: isActive ? '2px solid #4361ee' : 'none',
                outlineOffset: 2,
                transition: 'background 0.15s',
                flexShrink: 0,
              }}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
}
